import React, { useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import { useTranslation } from 'src/arb-wrapper';
import { useMyPermissions } from 'src/hooks/useMyPermissions';
import { PERMISSION_LIST_PAGE_SIZE } from 'src/store/constants';
import { usePlatformAdminControllerFindBusinessUnitsQuery } from 'src/store/leela-api';
import { GroupsTab } from './GroupsTab';
import { MembersTab } from './MembersTab';
import { PartnersTab } from './PartnersTab';

type AccessTab = 'groups' | 'members' | 'partners';

export const AccessManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    isPlatformAdmin,
    businessUnitAdminOf,
    isLoading,
    isError,
  } = useMyPermissions();
  const [activeTab, setActiveTab] = useState<AccessTab>('groups');
  const [selectedBuId, setSelectedBuId] = useState<string>('');

  // Platform admins need to fetch all BUs since businessUnitAdminOf is empty for them
  // BU admins already have name + countries from the my-permissions response
  const { data: allBusData, isError: isBusError, isLoading: isBusLoading } = usePlatformAdminControllerFindBusinessUnitsQuery(
    { page: 1, pageSize: PERMISSION_LIST_PAGE_SIZE },
    { skip: !isPlatformAdmin },
  );

  // Determine the available BU list based on role
  const availableBuIds = isPlatformAdmin
    ? ((allBusData as { data?: Array<{ id: string; name: string }> })?.data ?? []).map(bu => bu.id)
    : businessUnitAdminOf.map(bu => bu.id);

  const allBus = ((allBusData as { data?: Array<{ id: string; name: string; countries?: string[] }> })?.data ?? []);
  const availableBuOptions = isPlatformAdmin
    ? allBus.map(bu => ({ id: bu.id, label: bu.name, countries: bu.countries ?? [] }))
    : businessUnitAdminOf.map(bu => ({ id: bu.id, label: bu.name, countries: bu.countries as string[] ?? [] }));

  // Auto-select the first BU once available
  React.useEffect(() => {
    if (!selectedBuId && availableBuIds.length > 0) {
      setSelectedBuId(availableBuIds[0]);
    }
  }, [availableBuIds, selectedBuId]);

  if (isLoading || isBusLoading) return <Loader />;
  if (isError || isBusError) return <Alert type="error">{t('access_management_load_error')}</Alert>;

  if (!isPlatformAdmin && businessUnitAdminOf.length === 0) {
    return (
      <Column spacing="400">
        <Heading level={3}>{t('access_management_title')}</Heading>
        <Text>{t('access_management_no_access')}</Text>
      </Column>
    );
  }

  if (availableBuOptions.length === 0) {
    return (
      <Column spacing="400">
        <Heading level={3}>{t('access_management_title')}</Heading>
        <Text>{t('access_management_no_business_units')}</Text>
        <Link data-testid="access-management-create-bu-link" href="/bu-management">
          {t('bu_management')}
        </Link>
      </Column>
    );
  }

  const selectedBuCountries = availableBuOptions.find(
    bu => bu.id === selectedBuId,
  )?.countries ?? [];

  return (
    <Column data-testid="access-management-page" spacing="400">
      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <Heading level={3}>{t('access_management_title')}</Heading>
        {availableBuOptions.length > 1 && (
          <Select
            data-testid="access-management-bu-selector"
            size="medium"
            value={selectedBuId}
            onChange={(value: string) => { setSelectedBuId(value); }}
          >
            {availableBuOptions.map(bu => (
              <SelectOption key={bu.id} label={bu.label} value={bu.id} />
            ))}
          </Select>
        )}
      </Row>

      <TabGroup fill="line" value={activeTab} onChange={(tab: string) => { setActiveTab(tab as AccessTab); }}>
        <Tab data-testid="access-tab-groups" value="groups">
          {t('access_management_tab_groups')}
        </Tab>
        <Tab data-testid="access-tab-members" value="members">
          {t('access_management_tab_members')}
        </Tab>
        <Tab data-testid="access-tab-partners" value="partners">
          {t('access_management_tab_partners')}
        </Tab>
      </TabGroup>

      {selectedBuId && activeTab === 'groups' && <GroupsTab businessUnitCountries={selectedBuCountries} businessUnitId={selectedBuId} />}
      {selectedBuId && activeTab === 'members' && <MembersTab businessUnitId={selectedBuId} />}
      {selectedBuId && activeTab === 'partners' && <PartnersTab businessUnitId={selectedBuId} />}
    </Column>
  );
};
